/**
 * Cross-workspace types for the payment writer layer.
 *
 * Writer implementations live in apps/api/src/services/accounting/payments/;
 * AI engine tools call into the writer via a `paymentWriter` object injected
 * through LangGraph `config.configurable` (no cross-workspace imports).
 */

import type { AdjustmentType } from '@eriko/db';

export type { AdjustmentType };

export type PaymentMethod = 'CHECK' | 'CARD' | 'ACH' | 'CASH' | 'EFT' | 'OTHER';

export interface Allocation {
  /** AccountingEntity.id of the Bill/Invoice being settled */
  documentId: string;
  amount: number;
}

export interface PaymentInputBase {
  partyName: string;
  totalAmount: number;
  bankAccountId?: string;
  bankAccountHint?: string;
  paymentMethod?: PaymentMethod;
  allocations?: Allocation[];
  date?: string;
  currency?: string;
  exchangeRate?: number;
  reference?: string;
  adjustment?: { amount: number; type: AdjustmentType; description?: string };
}

export type PaymentInput = PaymentInputBase;
export type BillPaymentInput = PaymentInputBase;

export type PaymentResultKind = 'success' | 'partial' | 'skipped' | 'error';

export interface PaymentResult {
  kind: PaymentResultKind;
  externalId?: string;
  childExternalIds?: string[];
  entityId?: string;
  raw?: unknown;
  warning?: string;
  error?: string;
  requiresManualCleanup?: boolean;
  cleanupHints?: Record<string, string>;
}

export interface ResolvedAllocationRef {
  documentId: string;
  externalId: string;
  entityType: string; // 'Bill' | 'Invoice'
  amount: number;
}

/**
 * A bank/credit account resolved by the tool layer, passed into the
 * writer. The writer needs both the Eriko-internal id (for LOCAL
 * Transaction rows) and the provider externalId (for QBO/Xero refs).
 */
export interface BankAccountRef {
  id: string;          // AccountingEntity.id (Eriko-internal)
  externalId: string;  // QBO Account Id or Xero AccountID
  name: string;
  accountType: string; // 'Bank' | 'CreditCard' | ...
}

/**
 * Arguments accepted by every PaymentWriter method. The writer infers
 * provider from the active AccountingConnection for the org.
 */
export interface PaymentWriterCallArgs {
  orgId: string;
  party: { externalId: string; displayName: string };
  bankAccount: BankAccountRef;
  method: PaymentMethod;
  totalAmount: number;
  allocations: ResolvedAllocationRef[];
  date: string;
  currency: string;
  homeCurrency: string;
  exchangeRate?: number;
  reference?: string;
  adjustment?: { amount: number; type: AdjustmentType; description?: string };
  /** LOCAL-mode only: Eriko-internal BankAccount table id (separate from the AccountingEntity row). */
  bankAccountInternalId?: string;
  /** LOCAL-mode only: contra-side Category id (AR for Payment, AP for BillPayment). */
  contraCategoryId?: string;
  /**
   * Idempotency token (P1 review C2). Forwarded by the writer to the provider
   * client so a retried write does not create a duplicate Payment/BillPayment:
   * QBO → `Request-Id` header, Xero → `Idempotency-Key` header. For multi-leg
   * Xero writes, derive a distinct stable key per leg from this base. Set to a
   * stable value by the caller (e.g. ReconciliationMatch.id for reconciliation
   * writeback, or a hash of party+date+amount+allocations for chat tools).
   */
  idempotencyKey?: string;
}

/**
 * A line item for tools that post category-keyed amounts (Purchase,
 * SalesReceipt, VendorCredit, CreditMemo). For QBO/Xero this points to a
 * real GL account; for LOCAL it falls through to an Eriko Category.
 */
export interface PaymentLineItem {
  /** AccountingEntity.id of the GL account (Account row) — preferred when known */
  accountId?: string;
  /**
   * AccountingEntity.externalId of the GL account. For QBO this is the numeric
   * Account Id used directly as `AccountRef.value`. For XERO this is the Account
   * *AccountID GUID* — which is NOT what a Xero LineItem wants; use `accountCode`
   * for Xero line writes (XW-2/XPW-5).
   */
  accountExternalId?: string;
  /**
   * Xero nominal account *Code* (e.g. "420") — the value a Xero LineItem's
   * `AccountCode` field expects. Resolved from the synced Account's `data.Code`.
   * Unused by QBO (which references the account by id via `accountExternalId`).
   */
  accountCode?: string;
  /** LOCAL fallback: Eriko Category.id (when no AccountingEntity match) */
  categoryId?: string;
  amount: number;
  description?: string;
  taxAmount?: number;
  /**
   * Provider tax code applied to this line (QBO TaxCodeRef value / Xero TaxType).
   * REQUIRED for VAT-registered companies (UK etc.) — QBO rejects untaxed lines
   * with "Make sure all your transactions have a VAT rate". Omitted for US
   * (automated sales tax). When any line carries it, the QBO writer also sets
   * GlobalTaxCalculation='TaxExcluded'.
   */
  taxCode?: string;
}

export interface PurchaseCallArgs {
  orgId: string;
  /** Vendor (optional — a cash receipt may not have an Eriko Vendor entity) */
  party?: { externalId: string; displayName: string };
  /** Free-text payee name when no Vendor entity exists (Starbucks-style) */
  payeeName?: string;
  bankAccount: BankAccountRef;
  bankAccountInternalId?: string;
  method: PaymentMethod;
  totalAmount: number;
  lines: PaymentLineItem[];
  date: string;
  currency: string;
  homeCurrency: string;
  exchangeRate?: number;
  reference?: string;
  memo?: string;
  /** Idempotency token — provider client reuses it on retry so a retried POST can't double-book (XPW-2). */
  idempotencyKey?: string;
}

export interface SalesReceiptCallArgs {
  orgId: string;
  party?: { externalId: string; displayName: string };
  payeeName?: string;
  bankAccount: BankAccountRef;
  bankAccountInternalId?: string;
  method: PaymentMethod;
  totalAmount: number;
  lines: PaymentLineItem[];
  date: string;
  currency: string;
  homeCurrency: string;
  exchangeRate?: number;
  reference?: string;
  memo?: string;
  /** Idempotency token — provider client reuses it on retry so a retried POST can't double-book (XPW-2). */
  idempotencyKey?: string;
}

export interface RefundReceiptCallArgs {
  orgId: string;
  party: { externalId: string; displayName: string }; // customer
  bankAccount: BankAccountRef;
  bankAccountInternalId?: string;
  method: PaymentMethod;
  totalAmount: number;
  /** Xero: the original ACCREC invoice id to allocate the CreditNote against */
  originalInvoiceExternalId?: string;
  /** QBO: the CreditMemo entity id to LinkedTxn against (optional) */
  creditMemoExternalId?: string;
  lines?: PaymentLineItem[]; // when no prior CreditMemo, refund line items
  date: string;
  currency: string;
  homeCurrency: string;
  exchangeRate?: number;
  reference?: string;
}

export interface CreditCallArgs {
  orgId: string;
  party: { externalId: string; displayName: string }; // vendor (VendorCredit) or customer (CreditMemo)
  totalAmount: number;
  lines: PaymentLineItem[];
  date: string;
  currency: string;
  homeCurrency: string;
  exchangeRate?: number;
  reference?: string;
  memo?: string;
  /** Idempotency token — provider client reuses it on retry so a retried POST can't duplicate the credit (XPW-3). */
  idempotencyKey?: string;
}

export interface DepositCallArgs {
  orgId: string;
  bankAccount: BankAccountRef;
  bankAccountInternalId?: string;
  totalAmount: number;
  /** Existing Payment AccountingEntity ids to bundle into the deposit */
  paymentEntityIds?: string[];
  /** Additional non-AR income lines (e.g. interest, owner contribution) */
  lines?: PaymentLineItem[];
  date: string;
  currency: string;
  homeCurrency: string;
  exchangeRate?: number;
  reference?: string;
}

/**
 * Result returned by triggerReconciliationWriteback. Mirrors apps/api's
 * `WritebackResult` shape but typed loosely so ai-engine doesn't depend on
 * apps/api's internal type.
 */
export interface ReconciliationWritebackResult {
  kind: 'success' | 'skipped';
  provider?: 'QBO' | 'XERO';
  externalId?: string;
  entityId?: string;
  reason?: string;
  adjustmentWarning?: string;
}

/**
 * Injected via `config.configurable.triggerReconciliationWriteback`. apps/api
 * wires this up to call `writeReconciliationPayment(tx, match)` so the AI
 * engine's confirm_match tool can fire the same writeback path that the
 * reviewer-UI confirm route uses — without importing apps/api directly.
 *
 * Caller MUST pass the same `tx` it's already running inside, so the writeback
 * happens atomically with the match-status flip. If the writeback throws, the
 * caller's $transaction rolls back and the match stays SUGGESTED.
 */
export type TriggerReconciliationWriteback = (
  tx: unknown,
  match: Record<string, unknown>,
) => Promise<ReconciliationWritebackResult>;

/**
 * The shape injected into LangGraph `config.configurable.paymentWriter`.
 * AI engine tools call methods on this — they do not import the
 * implementation directly.
 */
export interface PaymentWriterInterface {
  recordPayment(args: PaymentWriterCallArgs): Promise<PaymentResult>;
  recordBillPayment(args: PaymentWriterCallArgs): Promise<PaymentResult>;
  recordPurchase(args: PurchaseCallArgs): Promise<PaymentResult>;
  recordSalesReceipt(args: SalesReceiptCallArgs): Promise<PaymentResult>;
  recordRefundReceipt(args: RefundReceiptCallArgs): Promise<PaymentResult>;
  recordVendorCredit(args: CreditCallArgs): Promise<PaymentResult>;
  recordCreditMemo(args: CreditCallArgs): Promise<PaymentResult>;
  recordDeposit(args: DepositCallArgs): Promise<PaymentResult>;
}

/**
 * Artifact data emitted by payment-creating tools, rendered inline in chat
 * by AssistantMessage.tsx in both apps/admin and apps/dashboard.
 */
export interface PaymentConfirmedArtifactData {
  kind: 'payment-confirmed';
  flavor:
    | 'payment_received'
    | 'bill_paid'
    | 'purchase_recorded'
    | 'sales_receipt_recorded'
    | 'refund_issued'
    | 'credit_recorded'
    | 'deposit_recorded';
  provider: 'QBO' | 'XERO' | 'LOCAL';
  partyName: string;
  totalAmount: number;
  currency: string;
  bankAccount?: { name: string };
  method?: string;
  date: string;
  allocations?: Array<{
    documentSummary: string;
    amount: number;
  }>;
  adjustment?: { amount: number; type: string; description?: string };
  /** Free-text warning surfaced by the writer (e.g. Xero refund only created
   *  the CreditNote — reviewer must manually allocate + pay out). */
  warning?: string;
  externalId: string;
  childExternalIds?: string[];
}

export function isPaymentConfirmedArtifactData(
  x: unknown,
): x is PaymentConfirmedArtifactData {
  return (
    typeof x === 'object' &&
    x !== null &&
    (x as { kind?: unknown }).kind === 'payment-confirmed'
  );
}
